import React, { useRef, useState, useEffect } from 'react';
import classnames from 'classnames';
import { Icon, Dropdown, DropdownProps, SearchBox, SearchBoxProps, List, Text } from 'tea-component';
import Loading from '@gp/components/Loading';
import useClickoutside from '@gp/hooks/useClickoutside';
import useCloseSearchBoxDropdownWhenScroll from '@gp/hooks/useCloseSearchBoxDropdownWhenScroll';
import useSearchKeyword from './_hooks/useSearchKeyword';
import useSearchRecords from './_hooks/useSearchRecords';

import type { IBaseProps } from '@home/types/common';

import './index.scss';

interface IRecordableSearchBoxProps extends IBaseProps {
  // 搜索记录业务类型（区分不同页面的搜索记录）
  domain: number;
  // SearchBox的input输入框的防抖时长
  inputDebounceWait?: number;
  // 搜索打点配置key
  searchLogKey?: string;
  // 删除搜索记录按钮打点配置key
  deleteSearchBtnLogKey?: string;
  // 是否禁用
  disabled?: boolean;
  // Dropdown组件props
  dropdownProps?: Omit<DropdownProps, 'button' | 'children'>;
  searchBoxProps?: SearchBoxProps;
  // 关键字
  value?: string;
  // 输入关键字时 进行搜索
  onChange?: (payload: string) => void;
  // 输入关键字或者点击搜索记录 进行搜索
  onSearch?: (payload: string) => Promise<any[]>;
  // 清空或者重置
  onClear?: () => void;
}

const RecordableSearchBox: React.FC<IRecordableSearchBoxProps> = (props) => {
  const {
    domain,
    inputDebounceWait,
    searchLogKey,
    deleteSearchBtnLogKey,
    disabled = false,
    dropdownProps,
    searchBoxProps,
    value,
    onChange,
    onSearch,
    onClear,
  } = props;

  const {
    className: dropdownClassName,
    overlayClassName: dropdownOverlayClassName,
    appearance: dropdownAppearance = 'pure',
    closeOnScroll: dropdownCloseOnScroll = true,
    matchButtonWidth: dropdownMatchButtonWidth = true,
    placement: dropdownPlacement = 'bottom-end',
    ...restDropdownProps
  } = dropdownProps || {};

  const { placeholder: searchBoxPlaceholder, size: searchBoxSize = 'l', ...restSearchBoxProps } = searchBoxProps || {};

  // Dropdown组件根节点dom
  const root = useRef(null);

  // 是否展示DropDown下拉弹窗
  const [showDropdown, setShowDropdown] = useState(false);

  // 搜索关键字
  const [keyword, setKeyword] = useState(value || '');

  const { onKeywordChange, onKeywordSearch, onKeywordClear } = useSearchKeyword({
    inputDebounceWait,
    keyword,
    setKeyword,
    onChange,
    onSearch: async (payload: string) => {
      try {
        const searchResult = await onSearch?.(payload);
        if (Array.isArray(searchResult) && searchResult.length > 0) {
          setShowDropdown(false);
          onPushRecord(payload);
        }
      } catch (exception) {}
    },
    onClear,
  });

  const { searchRecordsLoading, searchRecords, onPushRecord, onDeleteRecord } = useSearchRecords(domain, {
    searchLogKey,
    deleteSearchBtnLogKey,
  });

  const handleSearchBoxClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!searchRecordsLoading && Array.isArray(searchRecords) && searchRecords.length > 0) {
      setShowDropdown(true);
    }
  };

  useEffect(() => {
    if (value !== keyword) {
      setKeyword(value || '');
    }
  }, [value]);

  useClickoutside(root?.current, 'click', (e: any) => {
    if (e?.target?.classList?.contains('tea-icon-dismiss')) return;
    setShowDropdown(false);
  });

  useCloseSearchBoxDropdownWhenScroll(() => {
    setShowDropdown(false);
  });

  return (
    <Dropdown
      {...restDropdownProps}
      className={classnames('dbg-recordable-search-box-dropdown', dropdownClassName)}
      overlayClassName={classnames('dbg-recordable-search-box-dropdown-overlay', dropdownOverlayClassName)}
      ref={root}
      disabled={disabled}
      closeOnScroll={dropdownCloseOnScroll}
      matchButtonWidth={dropdownMatchButtonWidth}
      appearance={dropdownAppearance}
      placement={dropdownPlacement}
      open={!keyword && showDropdown}
      button={
        <SearchBox
          {...restSearchBoxProps}
          placeholder={searchBoxPlaceholder}
          size={searchBoxSize}
          disabled={disabled}
          value={keyword}
          onChange={onKeywordChange}
          onSearch={onKeywordSearch}
          onClear={onKeywordClear}
          onClick={handleSearchBoxClick}
        />
      }
    >
      <div className="dbg-initial-line-height">
        {searchRecordsLoading && <Loading style={{ height: 188 }} loading />}
        {!searchRecordsLoading && Array.isArray(searchRecords) && searchRecords.length > 0 && (
          <div>
            <Text className="dbg-recordable-search-box-label">Recent</Text>
            <List className="dbg-recordable-search-box-list">
              {searchRecords.map((searchRecord) => {
                const { id, name } = searchRecord || {};
                return (
                  <List.Item
                    className="dbg-recordable-search-box-item"
                    key={id}
                    onClick={(e) => {
                      e?.stopPropagation();
                      setShowDropdown(false);
                      onKeywordSearch(name);
                    }}
                  >
                    <div className="dbg-recordable-search-box-item-content">
                      <div>{name}</div>
                      <Icon
                        type="dismiss"
                        onClick={(e) => {
                          e?.stopPropagation();
                          onDeleteRecord(searchRecord);
                        }}
                      />
                    </div>
                  </List.Item>
                );
              })}
            </List>
          </div>
        )}
      </div>
    </Dropdown>
  );
};

export default RecordableSearchBox;
